import React, { useState } from 'react'
import { useManagement } from '../../contexts/ManagementContext'
import FileUpload from './FileUpload'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Star, 
  User, 
  Mail, 
  Phone, 
  Calendar,
  Eye,
  EyeOff,
  Award,
  Filter,
  X,
  Building2,
  Linkedin,
  Facebook,
  Twitter,
  Globe,
  Check
} from 'lucide-react'

const ManagementTeam = () => {
  const {
    members = [],
    stats,
    addMember,
    updateMember,
    deleteMember,
    bulkDeleteMembers,
    loading
  } = useManagement()

  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [selectedMembers, setSelectedMembers] = useState([])
  const [filterCategory, setFilterCategory] = useState('all')

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    category: 'Trust',
    email: '',
    phone: '',
    bio: '',
    qualifications: [''],
    experience: '',
    joinedDate: '',
    socialLinks: {
      linkedin: '',
      facebook: '',
      twitter: '',
      website: ''
    },
    order: 0,
    isActive: true,
    featured: false,
    image: null
  })

  const categories = [
    'Trust',
    'Teaching Staff'
  ]

  // Filter members with null checks
  const filteredMembers = (members || []).filter((member) => {
    const matchesSearch = 
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.category?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory = filterCategory === 'all' || member.category === filterCategory
    
    return matchesSearch && matchesCategory
  })

  // Stats calculation with null checks
  const statsData = {
    total: (members || []).length,
    active: (members || []).filter(m => m.isActive).length,
    featured: (members || []).filter(m => m.featured).length,
    byCategory: categories.reduce((acc, cat) => {
      acc[cat] = (members || []).filter(m => m.category === cat).length
      return acc
    }, {})
  }

  // Modal handlers
  const openModal = (member = null) => {
    setEditingMember(member)
    setIsModalOpen(true)
    
    if (member) {
      setFormData({
        name: member.name || '',
        position: member.position || '',
        category: member.category || 'Trust',
        email: member.email || '',
        phone: member.phone || '',
        bio: member.bio || '',
        qualifications: Array.isArray(member.qualifications) && member.qualifications.length > 0 ? member.qualifications : [''],
        experience: member.experience || '',
        socialLinks: {
          linkedin: member.socialLinks?.linkedin || '',
          facebook: member.socialLinks?.facebook || '',
          twitter: member.socialLinks?.twitter || '',
          website: member.socialLinks?.website || ''
        },
        joinedDate: member.joinedDate ? new Date(member.joinedDate).toISOString().split('T')[0] : '',
        order: member.order || 0,
        isActive: member.isActive !== undefined ? member.isActive : true,
        featured: member.featured !== undefined ? member.featured : false,
        image: null
      })
    } else {
      resetForm()
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingMember(null)
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      category: 'Teaching Staff',
      email: '',
      phone: '',
      bio: '',
      qualifications: [''],
      experience: '',
      joinedDate: '',
      socialLinks: {
        linkedin: '',
        facebook: '',
        twitter: '',
        website: ''
      },
      order: 0,
      isActive: true,
      featured: false,
      image: null
    })
  }

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSocialLinkChange = (platform, value) => {
    setFormData((prev) => ({
      ...prev,
      socialLinks: {
        ...(prev.socialLinks || {}),
        [platform]: value
      }
    }))
  }

  const handleQualificationChange = (index, value) => {
    const currentQualifications = formData.qualifications || ['']
    const newQualifications = [...currentQualifications]
    newQualifications[index] = value
    setFormData((prev) => ({
      ...prev,
      qualifications: newQualifications
    }))
  }

  const addQualification = () => {
    setFormData((prev) => ({
      ...prev,
      qualifications: [...(prev.qualifications || ['']), '']
    }))
  }

  const removeQualification = (index) => {
    setFormData((prev) => ({
      ...prev,
      qualifications: (prev.qualifications || ['']).filter((_, i) => i !== index)
    }))
  }

  const handleImageChange = (file) => {
    setFormData((prev) => ({
      ...prev,
      image: file
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const submitData = {
        ...formData,
        qualifications: (formData.qualifications || ['']).filter(q => q && q.trim() !== '')
      }

      if (editingMember && editingMember._id) {
        await updateMember(editingMember._id, submitData)
      } else {
        await addMember(submitData)
      }

      closeModal()
    } catch (error) {
      console.error('Error saving member:', error)
      alert(error.response?.data?.message || 'Failed to save management member')
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        await deleteMember(id)
      } catch (error) {
        console.error('Error deleting member:', error)
        alert('Failed to delete member')
      }
    }
  }

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedMembers.length} members?`)) {
      try {
        await bulkDeleteMembers(selectedMembers)
        setSelectedMembers([])
      } catch (error) {
        console.error('Error bulk deleting members:', error)
        alert('Failed to delete members')
      }
    }
  }

  const toggleSelectMember = (id) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((memberId) => memberId !== id) : [...prev, id]
    )
  }

  const selectAllMembers = () => {
    if (filteredMembers.length === 0) {
      setSelectedMembers([])
    } else if (selectedMembers.length === filteredMembers.length) {
      setSelectedMembers([])
    } else {
      setSelectedMembers(filteredMembers.map((m) => m._id))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-purple mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading management team...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Management Team</h1>
        <p className="text-gray-600">Manage school management and staff members</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Members</p>
              <p className="text-2xl font-bold text-gray-900">{statsData.total}</p>
            </div>
            <User className="w-8 h-8 text-primary-purple" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-primary-green">{statsData.active}</p>
            </div>
            <Eye className="w-8 h-8 text-primary-green" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Featured</p>
              <p className="text-2xl font-bold text-primary-yellow">{statsData.featured}</p>
            </div>
            <Star className="w-8 h-8 text-primary-yellow" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-2xl font-bold text-primary-blue">{categories.length}</p>
            </div>
            <Building2 className="w-8 h-8 text-primary-blue" />
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat} ({statsData.byCategory[cat] || 0})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            {selectedMembers.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete ({selectedMembers.length})
              </button>
            )}
            <button
              onClick={() => openModal()}
              className="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Member
            </button>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredMembers.length > 0 && selectedMembers.length === filteredMembers.length}
                    onChange={selectAllMembers}
                    className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Image</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Position</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Category</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No members found
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member._id)}
                        onChange={() => toggleSelectMember(member._id)}
                        className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {member.image?.url ? (
                        <img
                          src={member.image.url}
                          alt={member.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{member.name}</p>
                        {member.featured && (
                          <span className="inline-flex items-center gap-1 text-xs text-primary-yellow">
                            <Star className="w-3 h-3 fill-current" />
                            Featured
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{member.position}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-primary-blue/10 text-primary-blue">
                        {member.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">
                        {member.email && (
                          <div className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </div>
                        )}
                        {member.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {member.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-primary-green/10 text-primary-green">
                          <Eye className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                          <EyeOff className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openModal(member)}
                          className="p-2 text-primary-blue hover:bg-primary-blue/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingMember ? 'Edit Member' : 'Add New Member'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Joined Date
                    </label>
                    <input
                      type="date"
                      name="joinedDate"
                      value={formData.joinedDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="10-digit number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows="4"
                    maxLength="1000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {(formData.bio || '').length}/1000 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experience
                  </label>
                  <textarea
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                  />
                </div>
              </div>

              {/* Qualifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Qualifications</h3>
                  <button
                    type="button"
                    onClick={addQualification}
                    className="px-3 py-1 text-sm bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90 transition-colors"
                  >
                    + Add Qualification
                  </button>
                </div>

                {(formData.qualifications || ['']).map((qual, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={qual || ''}
                      onChange={(e) => handleQualificationChange(index, e.target.value)}
                      placeholder="e.g., M.A. in Education"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                    {(formData.qualifications || ['']).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeQualification(index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Social Links</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Linkedin className="w-4 h-4 inline mr-1" />
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={formData.socialLinks?.linkedin || ''}
                      onChange={(e) => handleSocialLinkChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Facebook className="w-4 h-4 inline mr-1" />
                      Facebook
                    </label>
                    <input
                      type="url"
                      value={formData.socialLinks?.facebook || ''}
                      onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                      placeholder="https://facebook.com/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Twitter className="w-4 h-4 inline mr-1" />
                      Twitter
                    </label>
                    <input
                      type="url"
                      value={formData.socialLinks?.twitter || ''}
                      onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                      placeholder="https://twitter.com/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.socialLinks?.website || ''}
                      onChange={(e) => handleSocialLinkChange('website', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Profile Image</h3>
                <FileUpload
                  onFileSelect={handleImageChange}
                  currentImageUrl={editingMember?.image?.url}
                />
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                        className="rounded border-gray-300 text-primary-purple focus:ring-primary-purple"
                      />
                      <span className="text-sm font-medium text-gray-700">Featured</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90 transition-colors flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {editingMember ? 'Update Member' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ManagementTeam
